import { baseApi } from './baseApi';

export const adminApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    adminLogin: builder.mutation({
      query: (credentials) => ({
        url: '/admin/login',
        method: 'POST',
        body: credentials,
      }),
    }),
    adminLogout: builder.mutation({
      query: () => ({
        url: '/admin/logout',
        method: 'POST',
      }),
    }),
    getAdminDashboard: builder.query({
      query: () => '/admin/dashboard',
      providesTags: ['Admin'],
    }),
    getTradeDashboard: builder.query({
      query: () => '/admin/trade-dashboard',
      providesTags: ['Admin'],
    }),
    getAdminAccount: builder.query({
      query: () => '/admin/account',
      providesTags: ['Admin'],
    }),
    updateAdminAccount: builder.mutation({
      query: (data) => {
        if (data instanceof FormData) {
          data.append('_method', 'PUT');
          return { url: '/admin/update-account', method: 'POST', body: data };
        }
        return { url: '/admin/update-account', method: 'POST', body: { ...data, _method: 'PUT' } };
      },
      invalidatesTags: ['Admin'],
    }),
    getAdminSettings: builder.query({
      query: () => '/admin/settings',
      providesTags: ['Admin'],
    }),
    updateAdminSettings: builder.mutation({
      query: (data) => ({
        url: '/admin/update-settings',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Admin'],
    }),
    getContacts: builder.query({
      query: (params) => ({
        url: '/admin/contacts',
        params,
      }),
      providesTags: ['Admin'],
    }),
    getPartners: builder.query({
      query: (params) => ({
        url: '/admin/partners',
        params,
      }),
      providesTags: ['Admin'],
    }),
    getUsers: builder.query({
      query: (params) => ({
        url: '/admin/users',
        params,
      }),
      providesTags: ['User'],
    }),
    getUser: builder.query({
      query: (id) => `/admin/users/${id}`,
      providesTags: (result, error, id) => [{ type: 'User', id }],
    }),
    getAds: builder.query({
      query: (params) => ({
        url: '/admin/ads',
        params,
      }),
      providesTags: ['Ad'],
    }),
    createUser: builder.mutation({
      query: (data) => ({
        url: '/admin/users',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['User'],
    }),
    updateUser: builder.mutation({
      query: ({ id, data }) => {
        if (data instanceof FormData) {
          data.append('_method', 'PUT');
          return { url: `/admin/users/${id}`, method: 'POST', body: data };
        }
        return { url: `/admin/users/${id}`, method: 'POST', body: { ...data, _method: 'PUT' } };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'User', id }, 'User'],
    }),
    deleteUser: builder.mutation({
      query: (id) => ({
        url: `/admin/users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
    }),
    getReviews: builder.query({
      query: (params) => ({
        url: '/admin/reviews',
        params,
      }),
      providesTags: ['Review'],
    }),
    getReview: builder.query({
      query: (id) => `/admin/reviews/${id}`,
      providesTags: (result, error, id) => [{ type: 'Review', id }],
    }),
    updateReviewStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/admin/reviews/${id}/status`,
        method: 'POST',
        body: { status, _method: 'PUT' },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Review', id }, 'Review'],
    }),
    getSentNotifications: builder.query({
      query: (params) => ({
        url: '/admin/notifications',
        params,
      }),
      providesTags: ['Notification'],
    }),
    sendNotification: builder.mutation({
      query: (data) => ({
        url: '/admin/notifications',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Notification'],
    }),
    searchUsersForNotification: builder.query({
      query: (search) => ({
        url: '/admin/notifications/search-users',
        params: { search },
      }),
    }),
    getSystemNotifications: builder.query({
      query: (params) => ({
        url: '/admin/system-notifications',
        params,
      }),
      providesTags: ['SystemNotification'],
    }),
    getUnreadNotificationCount: builder.query({
      query: () => '/admin/system-notifications/unread-count',
      providesTags: ['SystemNotification'],
    }),
    markSystemNotificationRead: builder.mutation({
      query: (id) => ({
        url: `/admin/system-notifications/${id}/mark-read`,
        method: 'POST',
      }),
      invalidatesTags: ['SystemNotification'],
    }),
    getActivityLogs: builder.query({
      query: (params) => ({
        url: '/admin/activity-logs',
        params,
      }),
      providesTags: ['ActivityLog'],
    }),
    toggleUserStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/admin/users/${id}/toggle-status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'User', id }, 'User'],
    }),
    addWalletBalance: builder.mutation({
      query: ({ id, amount, type, note }) => ({
        url: `/admin/users/${id}/add-wallet`,
        method: 'POST',
        body: { amount, type, note },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'User', id }, 'User'],
    }),
    updateUserVerification: builder.mutation({
      query: ({ id, status, node, documents }) => {
        const formData = new FormData();
        formData.append('status', status);
        if (node) formData.append('node', node);
        if (documents && documents.length > 0) {
          documents.forEach((file: File) => formData.append('documents[]', file));
        }
        return {
          url: `/admin/users/${id}/verification`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'User', id }, 'User'],
    }),
    getDoctors: builder.query({
      query: (params) => ({
        url: '/admin/doctors',
        params,
      }),
      providesTags: ['Doctor'],
    }),
    getDoctor: builder.query({
      query: (id) => `/admin/doctors/${id}`,
      providesTags: (result, error, id) => [{ type: 'Doctor', id }],
    }),
    createDoctor: builder.mutation({
      query: (data) => ({
        url: '/admin/doctors',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Doctor'],
    }),
    updateDoctor: builder.mutation({
      query: ({ id, data }) => {
        if (data instanceof FormData) {
          data.append('_method', 'PUT');
          return { url: `/admin/doctors/${id}`, method: 'POST', body: data };
        }
        return { url: `/admin/doctors/${id}`, method: 'POST', body: { ...data, _method: 'PUT' } };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'Doctor', id }, 'Doctor'],
    }),
    deleteDoctor: builder.mutation({
      query: (id) => ({
        url: `/admin/doctors/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Doctor'],
    }),
    toggleDoctorStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/admin/doctors/${id}/toggle-status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Doctor', id }, 'Doctor'],
    }),
    getItems: builder.query({
      query: (params) => ({
        url: '/admin/items',
        params,
      }),
      providesTags: ['Item'],
    }),
    getItem: builder.query({
      query: (id) => `/admin/items/${id}`,
      providesTags: (result, error, id) => [{ type: 'Item', id }],
    }),
    createItem: builder.mutation({
      query: (data) => ({
        url: '/admin/items',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Item'],
    }),
    updateItem: builder.mutation({
      query: ({ id, data }) => {
        if (data instanceof FormData) {
          data.append('_method', 'PUT');
          return { url: `/admin/items/${id}`, method: 'POST', body: data };
        }
        return { url: `/admin/items/${id}`, method: 'POST', body: { ...data, _method: 'PUT' } };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'Item', id }, 'Item'],
    }),
    deleteItem: builder.mutation({
      query: (id) => ({
        url: `/admin/items/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Item'],
    }),
    toggleItemStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/admin/items/${id}/toggle-status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Item', id }, 'Item'],
    }),
    toggleItemPremium: builder.mutation({
      query: ({ id, is_premium }) => ({
        url: `/admin/premium-item/${id}`,
        method: 'POST',
        body: { is_premium },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Item', id }, 'Item'],
    }),
    getArticles: builder.query({
      query: (params) => ({
        url: '/admin/articles',
        params,
      }),
      providesTags: ['Article'],
    }),
    getArticle: builder.query({
      query: (id) => `/admin/articles/${id}`,
      providesTags: (result, error, id) => [{ type: 'Article', id }],
    }),
    createArticle: builder.mutation({
      query: (data) => ({
        url: '/admin/articles',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Article'],
    }),
    updateArticle: builder.mutation({
      query: ({ id, data }) => {
        if (data instanceof FormData) {
          data.append('_method', 'PUT');
          return { url: `/admin/articles/${id}`, method: 'POST', body: data };
        }
        return { url: `/admin/articles/${id}`, method: 'POST', body: { ...data, _method: 'PUT' } };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'Article', id }, 'Article'],
    }),
    deleteArticle: builder.mutation({
      query: (id) => ({
        url: `/admin/articles/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Article'],
    }),
    toggleArticleStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/admin/articles/${id}/toggle-status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Article', id }, 'Article'],
    }),
    toggleArticleFeatured: builder.mutation({
      query: ({ id, is_featured }) => ({
        url: `/admin/featured-article/${id}`,
        method: 'POST',
        body: { is_featured },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Article', id }, 'Article'],
    }),
    toggleArticlePremium: builder.mutation({
      query: ({ id, is_premium }) => ({
        url: `/admin/premium-article/${id}`,
        method: 'POST',
        body: { is_premium },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Article', id }, 'Article'],
    }),
    getExhibitions: builder.query({
      query: (params) => ({
        url: '/admin/exhibitions',
        params,
      }),
      providesTags: ['Exhibition'],
    }),
    getExhibition: builder.query({
      query: (id) => `/admin/exhibitions/${id}`,
      providesTags: (result, error, id) => [{ type: 'Exhibition', id }],
    }),
    createExhibition: builder.mutation({
      query: (data) => ({
        url: '/admin/exhibitions',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Exhibition'],
    }),
    updateExhibition: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/admin/exhibitions/${id}`,
        method: 'POST',
        body: { ...data, _method: 'PUT' },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Exhibition', id }, 'Exhibition'],
    }),
    deleteExhibition: builder.mutation({
      query: (id) => ({
        url: `/admin/exhibitions/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Exhibition'],
    }),
    toggleExhibitionStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/admin/exhibitions/${id}/toggle-status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Exhibition', id }, 'Exhibition'],
    }),
    toggleExhibitionPremium: builder.mutation({
      query: ({ id, is_premium }) => ({
        url: `/admin/premium-exhibition/${id}`,
        method: 'POST',
        body: { is_premium },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Exhibition', id }, 'Exhibition'],
    }),
    getActivities: builder.query({
      query: (params) => ({
        url: '/admin/activities',
        params,
      }),
      providesTags: ['Activity'],
    }),
    getActivity: builder.query({
      query: (id) => ({
        url: `/admin/activities/${id}`,
      }),
      providesTags: (result, error, id) => [{ type: 'Activity', id }],
    }),
    createActivity: builder.mutation({
      query: (data) => ({
        url: '/admin/activities',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Activity'],
    }),
    updateActivity: builder.mutation({
      query: ({ id, data }) => {
        if (data instanceof FormData) {
          data.append('_method', 'PUT');
          return { url: `/admin/activities/${id}`, method: 'POST', body: data };
        }
        return { url: `/admin/activities/${id}`, method: 'POST', body: { ...data, _method: 'PUT' } };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'Activity', id }, 'Activity'],
    }),
    deleteActivity: builder.mutation({
      query: (id) => ({
        url: `/admin/activities/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Activity'],
    }),
    toggleActivityPremium: builder.mutation({
      query: ({ id, is_premium }) => ({
        url: `/admin/premium-activity/${id}`,
        method: 'POST',
        body: { is_premium },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Activity', id }, 'Activity'],
    }),
    getPages: builder.query({
      query: (params) => ({
        url: '/admin/pages',
        params,
      }),
      providesTags: ['Page'],
    }),
    getPage: builder.query({
      query: (id) => `/admin/pages/${id}`,
      providesTags: (result, error, id) => [{ type: 'Page', id }],
    }),
    createPage: builder.mutation({
      query: (data) => ({
        url: '/admin/pages',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Page'],
    }),
    updatePage: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/admin/pages/${id}`,
        method: 'POST',
        body: { ...data, _method: 'PUT' },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Page', id }, 'Page'],
    }),
    deletePage: builder.mutation({
      query: (id) => ({
        url: `/admin/pages/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Page'],
    }),
    getPackages: builder.query({
      query: (params) => ({
        url: '/admin/packages',
        params,
      }),
      providesTags: ['Package'],
    }),
    getPackage: builder.query({
      query: (id) => `/admin/packages/${id}`,
      providesTags: (result, error, id) => [{ type: 'Package', id }],
    }),
    createPackage: builder.mutation({
      query: (data) => ({
        url: '/admin/packages',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Package'],
    }),
    updatePackage: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/admin/packages/${id}`,
        method: 'POST',
        body: { ...data, _method: 'PUT' },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Package', id }, 'Package'],
    }),
    deletePackage: builder.mutation({
      query: (id) => ({
        url: `/admin/packages/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Package'],
    }),
    togglePackageStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/admin/packages/${id}/toggle-status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Package', id }, 'Package'],
    }),
    getSubscriptionHistory: builder.query({
      query: (params) => ({
        url: '/admin/subscription-history',
        params,
      }),
      providesTags: ['Admin'],
    }),
    getStripeLogs: builder.query({
      query: (params) => ({
        url: '/admin/stripe-logs',
        params,
      }),
      providesTags: ['Admin'],
    }),
    getCategories: builder.query({
      query: (params) => ({
        url: '/admin/categories',
        params,
      }),
      providesTags: ['Category'],
    }),
    getCategory: builder.query({
      query: (id) => `/admin/categories/${id}`,
      providesTags: (result, error, id) => [{ type: 'Category', id }],
    }),
    createCategory: builder.mutation({
      query: (data) => ({
        url: '/admin/categories',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Category'],
    }),
    updateCategory: builder.mutation({
      query: ({ id, data }) => ({
        url: `/admin/categories/${id}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Category', id }, 'Category'],
    }),
    deleteCategory: builder.mutation({
      query: (id) => ({
        url: `/admin/categories/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Category'],
    }),
    getCategoryProperties: builder.query({
      query: (id) => `/admin/categories/${id}/properties`,
      providesTags: (result, error, id) => [{ type: 'Category', id }, 'Property'],
    }),
    updateCategoryProperties: builder.mutation({
      query: ({ id, data }) => ({
        url: `/admin/categories/${id}/properties`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Category', id }, 'Property'],
    }),
    deleteProperty: builder.mutation({
      query: (id) => ({
        url: `/admin/properties/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Property'],
    }),
    getCategorySubCategories: builder.query({
      query: (id) => `/admin/categories/${id}/subcategories`,
      providesTags: (result, error, id) => [{ type: 'Category', id }, 'SubCategory'],
    }),
    updateCategorySubCategories: builder.mutation({
      query: ({ id, data }) => ({
        url: `/admin/categories/${id}/subcategories`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Category', id }, 'SubCategory'],
    }),
    deleteSubCategory: builder.mutation({
      query: (id) => ({
        url: `/admin/subcategories/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SubCategory'],
    }),
    toggleCategoryStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/admin/categories/${id}/toggle-status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Category', id }, 'Category'],
    }),
    getAdCategories: builder.query({
      query: (params) => ({
        url: '/admin/ad-categories',
        params,
      }),
      providesTags: ['AdCategory'],
    }),
    getAdCategory: builder.query({
      query: (id) => `/admin/ad-categories/${id}`,
      providesTags: (result, error, id) => [{ type: 'AdCategory', id }],
    }),
    createAdCategory: builder.mutation({
      query: (data) => ({
        url: '/admin/ad-categories',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['AdCategory'],
    }),
    updateAdCategory: builder.mutation({
      query: ({ id, data }) => {
          return {
            url: `/admin/ad-categories/${id}`,
            method: 'POST',
            body: data,
          };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'AdCategory', id }, 'AdCategory'],
    }),
    deleteAdCategory: builder.mutation({
      query: (id) => ({
        url: `/admin/ad-categories/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AdCategory'],
    }),
    toggleAdCategoryStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/admin/ad-categories/${id}/toggle-status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'AdCategory', id }, 'AdCategory'],
    }),
    getAdTypes: builder.query({
      query: (params) => ({
        url: '/admin/ad-types',
        params,
      }),
      providesTags: ['AdType'],
    }),
    getAdType: builder.query({
      query: (id) => `/admin/ad-types/${id}`,
      providesTags: (result, error, id) => [{ type: 'AdType', id }],
    }),
    createAdType: builder.mutation({
      query: (data) => ({
        url: '/admin/ad-types',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['AdType'],
    }),
    updateAdType: builder.mutation({
      query: ({ id, data }) => {
        if (data instanceof FormData) {
          data.append('_method', 'PUT');
          return { url: `/admin/ad-types/${id}`, method: 'POST', body: data };
        }
        return { url: `/admin/ad-types/${id}`, method: 'POST', body: { ...data, _method: 'PUT' } };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'AdType', id }, 'AdType'],
    }),
    deleteAdType: builder.mutation({
      query: (id) => ({
        url: `/admin/ad-types/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AdType'],
    }),
    toggleAdTypeStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/admin/ad-types/${id}/toggle-status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'AdType', id }, 'AdType'],
    }),
    getStrategicAdTypes: builder.query({
      query: (params) => ({
        url: '/admin/strategic-ad-types',
        params,
      }),
      providesTags: ['StrategicAdType'],
    }),
    getStrategicAdType: builder.query({
      query: (id) => `/admin/strategic-ad-types/${id}`,
      providesTags: (result, error, id) => [{ type: 'StrategicAdType', id }],
    }),
    createStrategicAdType: builder.mutation({
      query: (data) => ({
        url: '/admin/strategic-ad-types',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['StrategicAdType'],
    }),
    updateStrategicAdType: builder.mutation({
      query: ({ id, data }) => {
        if (data instanceof FormData) {
          data.append('_method', 'PUT');
          return { url: `/admin/strategic-ad-types/${id}`, method: 'POST', body: data };
        }
        return { url: `/admin/strategic-ad-types/${id}`, method: 'POST', body: { ...data, _method: 'PUT' } };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'StrategicAdType', id }, 'StrategicAdType'],
    }),
    deleteStrategicAdType: builder.mutation({
      query: (id) => ({
        url: `/admin/strategic-ad-types/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['StrategicAdType'],
    }),
    toggleStrategicAdTypeStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/admin/strategic-ad-types/${id}/toggle-status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'StrategicAdType', id }, 'StrategicAdType'],
    }),
    // Ad Packages
    getAdPackages: builder.query({
      query: (params) => ({
        url: '/admin/ad-packages',
        params,
      }),
      providesTags: ['AdPackage'],
    }),
    getAdPackage: builder.query({
      query: (id) => `/admin/ad-packages/${id}`,
      providesTags: (result, error, id) => [{ type: 'AdPackage', id }],
    }),
    createAdPackage: builder.mutation({
      query: (data) => ({
        url: '/admin/ad-packages',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['AdPackage'],
    }),
    updateAdPackage: builder.mutation({
      query: ({ id, data }) => {
        if (data instanceof FormData) {
          data.append('_method', 'PUT');
          return { url: `/admin/ad-packages/${id}`, method: 'POST', body: data };
        }
        return { url: `/admin/ad-packages/${id}`, method: 'POST', body: { ...data, _method: 'PUT' } };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'AdPackage', id }, 'AdPackage'],
    }),
    deleteAdPackage: builder.mutation({
      query: (id) => ({
        url: `/admin/ad-packages/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AdPackage'],
    }),
    toggleAdPackageStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/admin/ad-packages/${id}/toggle-status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'AdPackage', id }, 'AdPackage'],
    }),
    getInterests: builder.query({
      query: (params) => ({
        url: '/admin/interests',
        params,
      }),
      providesTags: ['Interest'],
    }),
    getInterest: builder.query({
      query: (id) => `/admin/interests/${id}`,
      providesTags: (result, error, id) => [{ type: 'Interest', id }],
    }),
    createInterest: builder.mutation({
      query: (data) => ({
        url: '/admin/interests',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Interest'],
    }),
    updateInterest: builder.mutation({
      query: ({ id, data }) => {
        if (data instanceof FormData) {
          data.append('_method', 'PUT');
          return { url: `/admin/interests/${id}`, method: 'POST', body: data };
        }
        return { url: `/admin/interests/${id}`, method: 'POST', body: { ...data, _method: 'PUT' } };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'Interest', id }, 'Interest'],
    }),
    deleteInterest: builder.mutation({
      query: (id) => ({
        url: `/admin/interests/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Interest'],
    }),
    toggleInterestStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/admin/interests/${id}/toggle-status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Interest', id }, 'Interest'],
    }),
    // Custom Fields
    getCustomFields: builder.query({
      query: (categoryId) => `/admin/ad-categories/${categoryId}/custom-fields`,
      providesTags: (result, error, categoryId) => [{ type: 'CustomField', id: categoryId }],
    }),
    getCustomField: builder.query({
      query: (id) => `/admin/custom-fields/${id}`,
      providesTags: (result, error, id) => [{ type: 'CustomField', id }],
    }),
    createCustomField: builder.mutation({
      query: (data) => ({
        url: '/admin/custom-fields',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, data) => [{ type: 'CustomField', id: data.category_id }, 'CustomField'],
    }),
    updateCustomField: builder.mutation({
      query: ({ id, data }) => ({
        url: `/admin/custom-fields/${id}`,
        method: 'POST',
        body: { ...data, _method: 'PUT' },
      }),
      invalidatesTags: (result, error, { data }) => [{ type: 'CustomField', id: data.category_id }, 'CustomField'],
    }),
    deleteCustomField: builder.mutation({
      query: (id) => ({
        url: `/admin/custom-fields/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CustomField'],
    }),
    // Custom Filters
    getCustomFilters: builder.query({
      query: (categoryId) => `/admin/ad-categories/${categoryId}/custom-filters`,
      providesTags: (result, error, categoryId) => [{ type: 'CustomFilter', id: categoryId }],
    }),
    getCustomFilter: builder.query({
      query: (id) => `/admin/custom-filters/${id}`,
      providesTags: (result, error, id) => [{ type: 'CustomFilter', id }],
    }),
    createCustomFilter: builder.mutation({
      query: (data) => ({
        url: '/admin/custom-filters',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, data) => [{ type: 'CustomFilter', id: data.category_id }, 'CustomFilter'],
    }),
    updateCustomFilter: builder.mutation({
      query: ({ id, data }) => ({
        url: `/admin/custom-filters/${id}`,
        method: 'POST',
        body: { ...data, _method: 'PUT' },
      }),
      invalidatesTags: (result, error, { data }) => [{ type: 'CustomFilter', id: data.category_id }, 'CustomFilter'],
    }),
    deleteCustomFilter: builder.mutation({
      query: (id) => ({
        url: `/admin/custom-filters/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CustomFilter'],
    }),
    // Ads
    // getAds: builder.query({
    //   query: (params) => ({
    //     url: '/admin/ads',
    //     params,
    //   }),
    //   providesTags: ['Ad'],
    // }),
    getAd: builder.query({
      query: (id) => `/admin/ads/${id}`,
      providesTags: (result, error, id) => [{ type: 'Ad', id }],
    }),
    getAdCharts: builder.query({
      query: (id) => `/admin/ads/${id}/charts`,
      providesTags: (result, error, id) => [{ type: 'Ad', id }],
    }),
    createAd: builder.mutation({
      query: (data) => ({
        url: '/admin/ads',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Ad'],
    }),
    updateAd: builder.mutation({
      query: ({ id, data }) => {
        if (data instanceof FormData) {
          data.append('_method', 'PUT');
          return { url: `/admin/ads/${id}`, method: 'POST', body: data };
        }
        return { url: `/admin/ads/${id}`, method: 'POST', body: { ...data, _method: 'PUT' } };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'Ad', id }, 'Ad'],
    }),
    deleteAd: builder.mutation({
      query: (id) => ({
        url: `/admin/ads/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Ad'],
    }),
    getStrategicAd: builder.query({
      query: (id) => `/admin/strategic-ads/${id}`,
      providesTags: (result, error, id) => [{ type: 'Ad', id }],
    }),
    createStrategicAd: builder.mutation({
      query: (data) => ({
        url: '/admin/strategic-ads',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Ad'],
    }),
    updateStrategicAd: builder.mutation({
      query: ({ id, data }) => {
        if (data instanceof FormData) {
          data.append('_method', 'PUT');
          return { url: `/admin/strategic-ads/${id}`, method: 'POST', body: data };
        }
        return { url: `/admin/strategic-ads/${id}`, method: 'POST', body: { ...data, _method: 'PUT' } };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'Ad', id }, 'Ad'],
    }),
    toggleAdStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/admin/ads/${id}/toggle-status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Ad', id }, 'Ad'],
    }),
    bulkAdAction: builder.mutation({
      query: ({ ids, action }) => ({
        url: '/admin/ads/bulk-action',
        method: 'POST',
        body: { ids, action },
      }),
      invalidatesTags: ['Ad'],
    }),
    getTopics: builder.query({
      query: (params) => ({
        url: '/admin/topics',
        params,
      }),
      providesTags: ['Topic'],
    }),
    getTopic: builder.query({
      query: (id) => `/admin/topics/${id}`,
      providesTags: (result, error, id) => [{ type: 'Topic', id }],
    }),
    createTopic: builder.mutation({
      query: (data) => ({
        url: '/admin/topics',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Topic'],
    }),
    updateTopic: builder.mutation({
      query: ({ id, data }) => {
        if (data instanceof FormData) {
          data.append('_method', 'PUT');
          return { url: `/admin/topics/${id}`, method: 'POST', body: data };
        }
        return { url: `/admin/topics/${id}`, method: 'POST', body: { ...data, _method: 'PUT' } };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'Topic', id }, 'Topic'],
    }),
    deleteTopic: builder.mutation({
      query: (id) => ({
        url: `/admin/topics/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Topic'],
    }),
    toggleTopicStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/admin/topics/${id}/toggle-status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Topic', id }, 'Topic'],
    }),
    toggleTopicPremium: builder.mutation({
      query: ({ id, is_premium }) => ({
        url: `/admin/premium-topic/${id}`,
        method: 'POST',
        body: { is_premium },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Topic', id }, 'Topic'],
    }),
    getAllTopics: builder.query({
      query: () => '/admin/topics/all',
      providesTags: ['Admin'],
    }),
    getAllCategories: builder.query({
      query: () => '/admin/categories/all',
      providesTags: ['Admin'],
    }),
    getAllTags: builder.query({
      query: (lang = 'en') => ({
        url: '/admin/tags/all',
        params: { lang },
      }),
      providesTags: ['Admin'],
    }),
    getTags: builder.query({
      query: (params) => ({
        url: '/admin/tags',
        params,
      }),
      providesTags: ['Tag'],
    }),
    createTag: builder.mutation({
      query: (data) => ({
        url: '/admin/tags',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Tag'],
    }),
    updateTag: builder.mutation({
      query: ({ id, data }) => ({
        url: `/admin/tags/${id}`,
        method: 'POST',
        body: { ...data, _method: 'PUT' },
      }),
      invalidatesTags: ['Tag'],
    }),
    deleteTag: builder.mutation({
      query: (id) => ({
        url: `/admin/tags/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Tag'],
    }),
    getTagArticles: builder.query({
      query: (tagId) => ({
        url: '/admin/tag-articles',
        params: { tag_id: tagId },
      }),
      providesTags: ['Tag'],
    }),
    getSubCategoriesByCategory: builder.query({
      query: (categoryId) => ({
        url: '/getSubCategories',
        params: { category_id: categoryId },
      }),
      providesTags: ['Admin'],
    }),
    getCountries: builder.query({
      query: (params) => ({
        url: '/admin/countries',
        params,
      }),
      providesTags: ['Country'],
    }),
    getCountry: builder.query({
      query: (id) => `/admin/countries/${id}`,
      providesTags: (result, error, id) => [{ type: 'Country', id }],
    }),
    createCountry: builder.mutation({
      query: (data) => ({
        url: '/admin/countries',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Country'],
    }),
    updateCountry: builder.mutation({
      query: ({ id, data }) => ({
        url: `/admin/countries/${id}`,
        method: 'POST',
        body: { ...data, _method: 'PUT' },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Country', id }, 'Country'],
    }),
    deleteCountry: builder.mutation({
      query: (id) => ({
        url: `/admin/countries/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Country'],
    }),
    getCities: builder.query({
      query: (params) => ({
        url: '/admin/cities',
        params,
      }),
      providesTags: ['City'],
    }),
    getCity: builder.query({
      query: (id) => `/admin/cities/${id}`,
      providesTags: (result, error, id) => [{ type: 'City', id }],
    }),
    createCity: builder.mutation({
      query: (data) => ({
        url: '/admin/cities',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['City'],
    }),
    updateCity: builder.mutation({
      query: ({ id, data }) => ({
        url: `/admin/cities/${id}`,
        method: 'POST',
        body: { ...data, _method: 'PUT' },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'City', id }, 'City'],
    }),
    deleteCity: builder.mutation({
      query: (id) => ({
        url: `/admin/cities/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['City'],
    }),
    getBanners: builder.query({
      query: (params) => ({
        url: '/admin/banners',
        params,
      }),
      providesTags: ['Banner'],
    }),
    getBanner: builder.query({
      query: (id) => `/admin/banners/${id}`,
      providesTags: (result, error, id) => [{ type: 'Banner', id }],
    }),
    createBanner: builder.mutation({
      query: (data) => {
        const formData = new FormData();
        formData.append('title', data.title || '');
        formData.append('title_ar', data.title_ar || '');
        formData.append('link_to', data.link_to);
        formData.append('sort_order', data.sort_order || '0');
        if (data.link_id) {
          formData.append('link_id', data.link_id);
        }
        if (data.link_url) {
          formData.append('link_url', data.link_url);
        }
        if (data.image) {
          formData.append('image', data.image);
        }
        return {
          url: '/admin/banners',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Banner'],
    }),
    updateBanner: builder.mutation({
      query: ({ id, data }) => {
        const formData = new FormData();
        formData.append('_method', 'PUT');
        formData.append('title', data.title || '');
        formData.append('title_ar', data.title_ar || '');
        formData.append('link_to', data.link_to);
        formData.append('sort_order', data.sort_order || '0');
        if (data.link_id) {
          formData.append('link_id', data.link_id);
        }
        if (data.link_url) {
          formData.append('link_url', data.link_url);
        }
        if (data.image) {
          formData.append('image', data.image);
        }
        return {
          url: `/admin/banners/${id}`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['Banner'],
    }),
    deleteBanner: builder.mutation({
      query: (id) => ({
        url: `/admin/banners/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Banner'],
    }),
    getSystemUsers: builder.query({
      query: (params) => ({
        url: '/admin/system-users',
        params,
      }),
      providesTags: ['SystemUser'],
    }),
    getSystemUser: builder.query({
      query: (id) => `/admin/system-users/${id}`,
      providesTags: (result, error, id) => [{ type: 'SystemUser', id }],
    }),
    createSystemUser: builder.mutation({
      query: (data) => ({
        url: '/admin/system-users',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['SystemUser'],
    }),
    updateSystemUser: builder.mutation({
      query: ({ id, data }) => ({
        url: `/admin/system-users/${id}`,
        method: 'POST',
        body: { ...data, _method: 'PUT' },
      }),
      invalidatesTags: ['SystemUser'],
    }),
    deleteSystemUser: builder.mutation({
      query: (id) => ({
        url: `/admin/system-users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SystemUser'],
    }),
    toggleSystemUserStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/admin/system-users/${id}/toggle-status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: ['SystemUser'],
    }),
    getRoles: builder.query({
      query: (params) => ({
        url: '/admin/roles',
        params,
      }),
      providesTags: ['Role'],
    }),
    getRole: builder.query({
      query: (id) => `/admin/roles/${id}`,
      providesTags: (result, error, id) => [{ type: 'Role', id }],
    }),
    createRole: builder.mutation({
      query: (data) => ({
        url: '/admin/roles',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Role'],
    }),
    updateRole: builder.mutation({
      query: ({ id, data }) => ({
        url: `/admin/roles/${id}`,
        method: 'POST',
        body: { ...data, _method: 'PUT' },
      }),
      invalidatesTags: ['Role'],
    }),
    toggleRoleStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/admin/roles/${id}/toggle-status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: ['Role'],
    }),
    deleteRole: builder.mutation({
      query: (id) => ({
        url: `/admin/roles/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Role'],
    }),
    getRolePermissions: builder.query({
      query: (id) => `/admin/roles/${id}/permissions`,
      providesTags: (result, error, id) => [{ type: 'Role', id }],
    }),
    updateRolePermissions: builder.mutation({
      query: ({ id, permissions }) => ({
        url: `/admin/roles/${id}/permissions`,
        method: 'POST',
        body: { permissions, _method: 'PUT' },
      }),
      invalidatesTags: ['Role'],
    }),
    getPermissions: builder.query({
      query: (params) => ({
        url: '/admin/permissions',
        params,
      }),
      providesTags: ['Admin'],
    }),
    massAction: builder.mutation({
      query: ({ type, ...data }) => ({
        url: `/admin/mass-action/${type}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Admin'],
    }),
    // Voucher Batches (Fesilah Cards/Vouchers)
    getVoucherBatches: builder.query({
      query: (params) => ({
        url: '/admin/voucher-batches',
        params,
      }),
      providesTags: ['VoucherBatch'],
    }),
    getVoucherBatch: builder.query({
      query: (id) => `/admin/voucher-batches/${id}`,
      providesTags: (result, error, id) => [{ type: 'VoucherBatch', id }],
    }),
    createVoucherBatch: builder.mutation({
      query: (data) => ({
        url: '/admin/voucher-batches',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['VoucherBatch'],
    }),
    updateVoucherBatch: builder.mutation({
      query: ({ id, data }) => ({
        url: `/admin/voucher-batches/${id}`,
        method: 'POST',
        body: { ...data, _method: 'PUT' },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'VoucherBatch', id }, 'VoucherBatch'],
    }),
    deleteVoucherBatch: builder.mutation({
      query: (id) => ({
        url: `/admin/voucher-batches/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['VoucherBatch'],
    }),
    getBatchVouchers: builder.query({
      query: ({ batchId, params }) => ({
        url: `/admin/voucher-batches/${batchId}/vouchers`,
        params,
      }),
      providesTags: ['Voucher'],
    }),
    getVoucher: builder.query({
      query: (id) => `/admin/vouchers/${id}`,
      providesTags: (result, error, id) => [{ type: 'Voucher', id }],
    }),
    // Promo Codes
    getPromoCodes: builder.query({
      query: (params) => ({
        url: '/admin/promo-codes',
        params,
      }),
      providesTags: ['PromoCode'],
    }),
    getPromoCode: builder.query({
      query: (id) => `/admin/promo-codes/${id}`,
      providesTags: (result, error, id) => [{ type: 'PromoCode', id }],
    }),
    createPromoCode: builder.mutation({
      query: (data) => ({
        url: '/admin/promo-codes',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['PromoCode'],
    }),
    updatePromoCode: builder.mutation({
      query: ({ id, data }) => ({
        url: `/admin/promo-codes/${id}`,
        method: 'POST',
        body: { ...data, _method: 'PUT' },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'PromoCode', id }, 'PromoCode'],
    }),
    deletePromoCode: builder.mutation({
      query: (id) => ({
        url: `/admin/promo-codes/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PromoCode'],
    }),
    searchUsersForPromoCode: builder.query({
      query: (search) => ({
        url: '/admin/promo-codes/search-users',
        params: { search },
      }),
    }),
    // Agricultural Influencers
    getAgriculturalInfluencers: builder.query({
      query: (params) => ({
        url: '/admin/agricultural-influencers',
        params,
      }),
      providesTags: ['AgriculturalInfluencer'],
    }),
    getAgriculturalInfluencer: builder.query({
      query: (id) => `/admin/agricultural-influencers/${id}`,
      providesTags: (result, error, id) => [{ type: 'AgriculturalInfluencer', id }],
    }),
    createAgriculturalInfluencer: builder.mutation({
      query: (data) => ({
        url: '/admin/agricultural-influencers',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['AgriculturalInfluencer'],
    }),
    updateAgriculturalInfluencer: builder.mutation({
      query: ({ id, data }) => {
        if (data instanceof FormData) {
          data.append('_method', 'PUT');
          return { url: `/admin/agricultural-influencers/${id}`, method: 'POST', body: data };
        }
        return { url: `/admin/agricultural-influencers/${id}`, method: 'POST', body: { ...data, _method: 'PUT' } };
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'AgriculturalInfluencer', id }, 'AgriculturalInfluencer'],
    }),
    deleteAgriculturalInfluencer: builder.mutation({
      query: (id) => ({
        url: `/admin/agricultural-influencers/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AgriculturalInfluencer'],
    }),
    toggleAgriculturalInfluencerStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/admin/agricultural-influencers/${id}/toggle-status`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'AgriculturalInfluencer', id }, 'AgriculturalInfluencer'],
    }),
  }),
});

export const {
  useAdminLoginMutation,
  useAdminLogoutMutation,
  useGetAdminDashboardQuery,
  useGetTradeDashboardQuery,
  useGetAdminAccountQuery,
  useUpdateAdminAccountMutation,
  useGetAdminSettingsQuery,
  useUpdateAdminSettingsMutation,
  useGetContactsQuery,
  useGetPartnersQuery,
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useGetUserQuery,
  useGetAdsQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useToggleUserStatusMutation,
  useAddWalletBalanceMutation,
  useUpdateUserVerificationMutation,
  useGetDoctorsQuery,
  useGetDoctorQuery,
  useCreateDoctorMutation,
  useUpdateDoctorMutation,
  useDeleteDoctorMutation,
  useToggleDoctorStatusMutation,
  useGetItemsQuery,
  useGetItemQuery,
  useCreateItemMutation,
  useUpdateItemMutation,
  useDeleteItemMutation,
  useToggleItemStatusMutation,
  useToggleItemPremiumMutation,
  useGetArticlesQuery,
  useGetArticleQuery,
  useCreateArticleMutation,
  useUpdateArticleMutation,
  useDeleteArticleMutation,
  useToggleArticleStatusMutation,
  useToggleArticleFeaturedMutation,
  useToggleArticlePremiumMutation,
  useGetExhibitionsQuery,
  useGetExhibitionQuery,
  useCreateExhibitionMutation,
  useUpdateExhibitionMutation,
  useDeleteExhibitionMutation,
  useToggleExhibitionStatusMutation,
  useToggleExhibitionPremiumMutation,
  useGetActivitiesQuery,
  useGetActivityQuery,
  useCreateActivityMutation,
  useUpdateActivityMutation,
  useDeleteActivityMutation,
  useToggleActivityPremiumMutation,
  useGetPagesQuery,
  useGetPageQuery,
  useCreatePageMutation,
  useUpdatePageMutation,
  useDeletePageMutation,
  useGetPackagesQuery,
  useGetPackageQuery,
  useCreatePackageMutation,
  useUpdatePackageMutation,
  useDeletePackageMutation,
  useTogglePackageStatusMutation,
  useGetSubscriptionHistoryQuery,
  useGetStripeLogsQuery,
  useGetCategoriesQuery,
  useGetCategoryQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useToggleCategoryStatusMutation,
  useGetCategoryPropertiesQuery,
  useUpdateCategoryPropertiesMutation,
  useDeletePropertyMutation,
  useGetCategorySubCategoriesQuery,
  useUpdateCategorySubCategoriesMutation,
  useDeleteSubCategoryMutation,
  useGetAllCategoriesQuery,
  useGetSubCategoriesByCategoryQuery,
  useGetAdCategoriesQuery,
  useGetAdCategoryQuery,
  useCreateAdCategoryMutation,
  useUpdateAdCategoryMutation,
  useDeleteAdCategoryMutation,
  useToggleAdCategoryStatusMutation,
  useGetAdTypesQuery,
  useGetAdTypeQuery,
  useCreateAdTypeMutation,
  useUpdateAdTypeMutation,
  useDeleteAdTypeMutation,
  useToggleAdTypeStatusMutation,
  useGetStrategicAdTypesQuery,
  useGetStrategicAdTypeQuery,
  useCreateStrategicAdTypeMutation,
  useUpdateStrategicAdTypeMutation,
  useDeleteStrategicAdTypeMutation,
  useToggleStrategicAdTypeStatusMutation,
  useGetAdPackagesQuery,
  useGetAdPackageQuery,
  useCreateAdPackageMutation,
  useUpdateAdPackageMutation,
  useDeleteAdPackageMutation,
  useToggleAdPackageStatusMutation,
  useGetInterestsQuery,
  useGetInterestQuery,
  useCreateInterestMutation,
  useUpdateInterestMutation,
  useDeleteInterestMutation,
  useToggleInterestStatusMutation,
  useGetCustomFiltersQuery,
  useGetCustomFilterQuery,
  useCreateCustomFilterMutation,
  useUpdateCustomFilterMutation,
  useDeleteCustomFilterMutation,
  useGetCustomFieldsQuery,
  useGetCustomFieldQuery,
  useCreateCustomFieldMutation,
  useUpdateCustomFieldMutation,
  useDeleteCustomFieldMutation,
  useGetAdQuery,
  useGetAdChartsQuery,
  useCreateAdMutation,
  useUpdateAdMutation,
  useDeleteAdMutation,
  useToggleAdStatusMutation,
  useBulkAdActionMutation,
  useGetAllTagsQuery,
  useGetTagsQuery,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
  useGetTagArticlesQuery,
  useGetTopicsQuery,
  useGetTopicQuery,
  useCreateTopicMutation,
  useUpdateTopicMutation,
  useDeleteTopicMutation,
  useToggleTopicStatusMutation,
  useToggleTopicPremiumMutation,
  useGetAllTopicsQuery,
  useGetCountriesQuery,
  useGetCountryQuery,
  useCreateCountryMutation,
  useUpdateCountryMutation,
  useDeleteCountryMutation,
  useGetCitiesQuery,
  useGetCityQuery,
  useCreateCityMutation,
  useUpdateCityMutation,
  useDeleteCityMutation,
  useGetBannersQuery,
  useGetBannerQuery,
  useCreateBannerMutation,
  useUpdateBannerMutation,
  useDeleteBannerMutation,
  useGetSystemUsersQuery,
  useGetSystemUserQuery,
  useCreateSystemUserMutation,
  useUpdateSystemUserMutation,
  useDeleteSystemUserMutation,
  useToggleSystemUserStatusMutation,
  useGetRolesQuery,
  useGetRoleQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useToggleRoleStatusMutation,
  useDeleteRoleMutation,
  useGetRolePermissionsQuery,
  useUpdateRolePermissionsMutation,
  useGetPermissionsQuery,
  useMassActionMutation,
  useGetVoucherBatchesQuery,
  useGetVoucherBatchQuery,
  useCreateVoucherBatchMutation,
  useUpdateVoucherBatchMutation,
  useDeleteVoucherBatchMutation,
  useGetBatchVouchersQuery,
  useGetVoucherQuery,
  useGetPromoCodesQuery,
  useGetPromoCodeQuery,
  useCreatePromoCodeMutation,
  useUpdatePromoCodeMutation,
  useDeletePromoCodeMutation,
  useSearchUsersForPromoCodeQuery,
  useGetSystemNotificationsQuery,
  useGetUnreadNotificationCountQuery,
  useMarkSystemNotificationReadMutation,
  useGetAgriculturalInfluencersQuery,
  useGetAgriculturalInfluencerQuery,
  useCreateAgriculturalInfluencerMutation,
  useUpdateAgriculturalInfluencerMutation,
  useDeleteAgriculturalInfluencerMutation,
  useToggleAgriculturalInfluencerStatusMutation,
  useGetReviewsQuery,
  useGetReviewQuery,
  useUpdateReviewStatusMutation,
  useGetSentNotificationsQuery,
  useSendNotificationMutation,
  useSearchUsersForNotificationQuery,
} = adminApi;
